import type { PopperHeaderProps } from '@talos/kyoko';
import { CurrentDocumentProvider, Divider, Grid, PopperHeader, Stack, useCurrentWindow } from '@talos/kyoko';
import { useCallback, useEffect, useRef, useState } from 'react';
import { StyleSheetManager } from 'styled-components';
import { POPOUT_URL, STYLE_TAG_ID } from '../Layout/tokens';
import { addStyles } from '../Layout/utils/addStyles';
import { removeStyles } from '../Layout/utils/removeStyles';
import NewWindow from '../NewWindow';
import { MarketSelectorBlotter } from './blotter/MarketSelectorBlotter';
import { useMarketSelectorContext } from './MarketSelectorContext';
import { MarketSelectorPresetsList } from './presets/MarketSelectorPresetsList';
import {
  BASE_ADVANCED_MARKET_SELECTOR_WIDTH,
  DEFAULT_MARKET_SELECTOR_HEIGHT,
  MARKET_SELECTOR_POPOUT_WINDOW_NAME,
} from './tokens';
import { getAdditionalPanelWidthFromEnrichments } from './utils/enrichments';

const SPACING = 8;
export function MarketSelectorWindow({
  testID,
  isOpen,
  referenceElement,
  headerProps,
}: {
  testID?: string;
  isOpen: boolean;
  referenceElement: HTMLElement | null;
  headerProps: PopperHeaderProps | undefined;
}) {
  const { advancedMode, enrichments, narrowModeConfig, setIsOpen } = useMarketSelectorContext();
  const [popoutWindow, setPopoutWindow] = useState<Window | null>(null);
  const currentWindow = useCurrentWindow();
  const popoutWindowRef = useRef<Window | null>(null);
  const handleCloseRef = useRef<() => void>(() => {});
  const stylesObserverRef = useRef<MutationObserver | null>(null);

  // Zero deps keeps this handler stable for removeEventListener
  // handleCloseRef avoids capturing a stale handleClose in the closure
  const handlePopoutWindowKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCloseRef.current();
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (e.key === 'F5' || (e.metaKey && e.key === 'r')) {
      // Prevent default refresh behavior
      e.preventDefault();
    }
  }, []);

  const closePanel = useCallback(() => {
    narrowModeConfig.setShowMarketSelectorBlotter(false);
    setIsOpen(false);
  }, [narrowModeConfig, setIsOpen]);

  const handleClose = useCallback(() => {
    closePanel();
    const currentPopoutWindow = popoutWindowRef.current;
    if (currentPopoutWindow == null) {
      return;
    }
    stylesObserverRef.current?.disconnect();
    stylesObserverRef.current = null;
    currentPopoutWindow.removeEventListener('keydown', handlePopoutWindowKeyDown);
    popoutWindowRef.current = null;
    setPopoutWindow(null);
    currentPopoutWindow.close();
  }, [closePanel, handlePopoutWindowKeyDown]);
  handleCloseRef.current = handleClose;

  const handleOpen = useCallback(
    (popoutWindow: Window) => {
      popoutWindowRef.current = popoutWindow;
      setPopoutWindow(popoutWindow);
      popoutWindow.focus();
      const stylesObserver = new MutationObserver(() => {
        removeStyles(popoutWindow.document, STYLE_TAG_ID);
        addStyles(popoutWindow.document, document.styleSheets, STYLE_TAG_ID);
      });
      stylesObserverRef.current = stylesObserver;
      const styles = window.document.getElementById(STYLE_TAG_ID);
      if (styles) {
        stylesObserver.observe(styles, { childList: true, characterData: true, subtree: true });
      }

      popoutWindow.addEventListener('keydown', handlePopoutWindowKeyDown);
    },
    [handlePopoutWindowKeyDown]
  );

  useEffect(() => {
    return () => {
      stylesObserverRef.current?.disconnect();
    };
  }, []);

  const widthAddedByExtraColumns = getAdditionalPanelWidthFromEnrichments(advancedMode, enrichments);
  const boundingRect = referenceElement?.getBoundingClientRect();
  const windowWidth = BASE_ADVANCED_MARKET_SELECTOR_WIDTH + widthAddedByExtraColumns;
  const windowHeight = DEFAULT_MARKET_SELECTOR_HEIGHT;
  const windowLeft =
    currentWindow.screenLeft - windowWidth < 0
      ? currentWindow.screenLeft + currentWindow.outerWidth + SPACING
      : currentWindow.screenLeft - windowWidth - SPACING;
  const windowTop = boundingRect?.top ?? 0 + (boundingRect?.height ?? 0) + currentWindow.screenTop - windowHeight;

  return isOpen ? (
    <NewWindow
      url={POPOUT_URL}
      copyStyles={true}
      name={MARKET_SELECTOR_POPOUT_WINDOW_NAME}
      features={{ width: windowWidth, height: windowHeight, left: windowLeft, top: windowTop }}
      onUnload={handleClose}
      onBlock={handleClose}
      onOpen={handleOpen}
      title="Market Selector"
    >
      <CurrentDocumentProvider
        currentDocument={popoutWindow?.document ?? document}
        currentWindow={popoutWindow ?? window}
      >
        <StyleSheetManager target={popoutWindow?.document.head}>
          <Stack h="100%">
            <PopperHeader {...headerProps} onCloseClicked={handleClose} />
            <Grid
              flex={1}
              gridTemplateColumns="200px min-content auto"
              data-testid={testID ?? 'market-selector-panel'}
              background="gray.040"
            >
              <MarketSelectorPresetsList />
              <Divider orientation="vertical" my="0" color="backgroundDivider" />
              <MarketSelectorBlotter />
            </Grid>
          </Stack>
        </StyleSheetManager>
      </CurrentDocumentProvider>
    </NewWindow>
  ) : null;
}
